import React, { useState } from 'react';
import Card from '../components/ProfileCard.tsx';
import Button from '../components/Button';
import Badge from '../components/Badge';
import SearchInput from '../components/SearchInput';
import Pagination from '../components/Pagination';
import { MessageSquare, Filter, Clock  } from 'lucide-react';

const SupportTickets: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filterStatus, setFilterStatus] = useState('All');
    const [expandedTicket, setExpandedTicket] = useState<number | null>(null);

    // Sample data
    const tickets = [
        {
            id: 1,
            userName: 'John Smith',
            email: 'john.smith@example.com',
            subject: 'Installation Issue with E-commerce Template',
            templateName: 'E-commerce Pro',
            message: 'I\'m having trouble installing the template on my WordPress site. The styles are not loading correctly and some of the JavaScript functionality seems to be broken. I\'ve followed all the installation instructions in the documentation.',
            purchaseDetails: {
                date: '2025-02-10',
                orderId: 'ORD-12345',
                projectUrl: 'https://mysite.example.com',
            },
            createdAt: '2025-03-01',
            status: 'Open',
            responses: [
                {
                    id: 1,
                    from: 'Support Team',
                    message: 'Thank you for reaching out. Could you please provide your browser console logs and confirm which version of WordPress you\'re using?',
                    createdAt: '2025-03-02',
                },
                {
                    id: 2,
                    from: 'John Smith',
                    message: 'I\'m using WordPress 6.3 and I\'ve attached the console logs. The main error seems to be related to jQuery not being loaded.',
                    createdAt: '2025-03-03',
                },
            ],
        },
        {
            id: 2,
            userName: 'Sarah Johnson',
            email: 'sarah.johnson@example.com',
            subject: 'Customization Help for Portfolio Template',
            templateName: 'Portfolio Plus',
            message: 'I need help customizing the color scheme of the Portfolio Plus template. I want to match it with my brand colors but I\'m not sure which files I need to modify.',
            purchaseDetails: {
                date: '2025-01-15',
                orderId: 'ORD-67890',
                projectUrl: 'https://sarahjohnson.example.com',
            },
            createdAt: '2025-03-05',
            status: 'In Progress',
            responses: [
                {
                    id: 1,
                    from: 'Support Team',
                    message: 'Hello Sarah, you can modify the colors in the _variables.scss file. Would you like us to provide more specific guidance?',
                    createdAt: '2025-03-06',
                },
            ],
        },
        {
            id: 3,
            userName: 'Michael Brown',
            email: 'michael.brown@example.com',
            subject: 'Feature Request for Blog Template',
            templateName: 'Blog Master',
            message: 'I love the Blog Master template, but I think it would be even better with a dark mode option. Would it be possible to add this feature in a future update?',
            purchaseDetails: {
                date: '2025-02-20',
                orderId: 'ORD-24680',
                projectUrl: 'https://michaelblog.example.com',
            },
            createdAt: '2025-03-08',
            status: 'Open',
            responses: [],
        },
        {
            id: 4,
            userName: 'Emily Davis',
            email: 'emily.davis@example.com',
            subject: 'Refund Request',
            templateName: 'Landing Page Pro',
            message: 'I purchased the Landing Page Pro template but it doesn\'t have the features that were advertised. I would like to request a refund.',
            purchaseDetails: {
                date: '2025-03-01',
                orderId: 'ORD-13579',
                projectUrl: 'N/A',
            },
            createdAt: '2025-03-10',
            status: 'Resolved',
            responses: [
                {
                    id: 1,
                    from: 'Support Team',
                    message: 'We\'re sorry to hear about your experience. Could you please specify which features are missing?',
                    createdAt: '2025-03-11',
                },
                {
                    id: 2,
                    from: 'Emily Davis',
                    message: 'The advertisement mentioned a form builder and A/B testing features, but I can\'t find them in the template.',
                    createdAt: '2025-03-12',
                },
                {
                    id: 3,
                    from: 'Support Team',
                    message: 'You\'re right, and we apologize for the confusion. We\'ve processed your refund, and it should appear in your account within 3-5 business days.',
                    createdAt: '2025-03-13',
                },
            ],
        },
        {
            id: 5,
            userName: 'David Wilson',
            email: 'david.wilson@example.com',
            subject: 'Mobile Responsiveness Issue',
            templateName: 'Admin Dashboard',
            message: 'The Admin Dashboard template is not displaying correctly on mobile devices. The navigation menu overlaps with the content area.',
            purchaseDetails: {
                date: '2025-02-25',
                orderId: 'ORD-97531',
                projectUrl: 'https://davidadmin.example.com',
            },
            createdAt: '2025-03-15',
            status: 'Open',
            responses: [],
        },
    ];

    const statusColors = {
        'Open': 'warning',
        'In Progress': 'info',
        'Resolved': 'success',
        'Closed': 'secondary',
    } as const;

    // Filter and search
    const filteredTickets = tickets.filter((ticket) => {
        const matchesSearch =
            ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.templateName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterStatus === 'All' || ticket.status === filterStatus;

        return matchesSearch && matchesFilter;
    });

    // Pagination
    const itemsPerPage = 5;
    const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
    const paginatedTickets = filteredTickets.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const toggleTicket = (id: number) => {
        setExpandedTicket(expandedTicket === id ? null : id);
    };

    const [newResponse, setNewResponse] = useState('');

    const handleSubmitResponse = (ticketId: number) => {
        // In a real app, this would send the response to an API
        console.log(`Submitting response for ticket ${ticketId}: ${newResponse}`);
        setNewResponse('');
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Manage and respond to customer support requests
                </p>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <SearchInput
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="md:w-64"
                />

                <div className="flex items-center space-x-2">
                    <Filter size={20} className="text-gray-400" />
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                        <option value="All">All Statuses</option>
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                    </select>
                </div>
            </div>

            <div className="space-y-4">
                {paginatedTickets.length > 0 ? (
                    paginatedTickets.map((ticket) => (
                        <Card key={ticket.id} className="hover:shadow-md transition-shadow">
                            <div
                                className="flex flex-col space-y-4"
                                onClick={() => toggleTicket(ticket.id)}
                            >
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center">
                                            <h3 className="text-lg font-medium text-gray-900">{ticket.subject}</h3>
                                            <Badge
                                                variant={statusColors[ticket.status as keyof typeof statusColors]}
                                                className="ml-2"
                                            >
                                                {ticket.status}
                                            </Badge>
                                        </div>
                                        <p className="mt-1 text-sm text-gray-500">
                                            From: {ticket.userName} ({ticket.email})
                                        </p>
                                        <p className="mt-1 text-sm text-gray-500">
                                            Template: {ticket.templateName}
                                        </p>
                                        <div className="mt-2 flex items-center text-sm text-gray-500">
                                            <Clock size={14} className="mr-1" />
                                            <span>Created: {ticket.createdAt}</span>
                                            <span className="mx-2">•</span>
                                            <MessageSquare size={14} className="mr-1" />
                                            <span>{ticket.responses.length} responses</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <Button
                                            variant={expandedTicket === ticket.id ? "primary" : "secondary"}
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleTicket(ticket.id);
                                            }}
                                        >
                                            {expandedTicket === ticket.id ? "Hide Details" : "View Details"}
                                        </Button>

                                        {ticket.status !== 'Resolved' && ticket.status !== 'Closed' && (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    // Mark as resolved logic would go here
                                                }}
                                            >
                                                Mark Resolved
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {expandedTicket === ticket.id && (
                                    <div className="mt-4 space-y-4">
                                        <div className="bg-gray-50 p-4 rounded-md">
                                            <h4 className="text-sm font-medium text-gray-900">Original Message:</h4>
                                            <p className="mt-2 text-sm text-gray-700">{ticket.message}</p>

                                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div>
                                                    <h5 className="text-xs font-medium text-gray-500">Purchase Date</h5>
                                                    <p className="text-sm text-gray-900">{ticket.purchaseDetails.date}</p>
                                                </div>
                                                <div>
                                                    <h5 className="text-xs font-medium text-gray-500">Order ID</h5>
                                                    <p className="text-sm text-gray-900">{ticket.purchaseDetails.orderId}</p>
                                                </div>
                                                <div>
                                                    <h5 className="text-xs font-medium text-gray-500">Project URL</h5>
                                                    <p className="text-sm text-gray-900">
                                                        {ticket.purchaseDetails.projectUrl === 'N/A' ? (
                                                            'N/A'
                                                        ) : (
                                                            <a
                                                                href={ticket.purchaseDetails.projectUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:text-blue-800"
                                                            >
                                                                {ticket.purchaseDetails.projectUrl}
                                                            </a>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {ticket.responses.length > 0 && (
                                            <div className="space-y-4">
                                                <h4 className="text-sm font-medium text-gray-900">Conversation:</h4>
                                                {ticket.responses.map((response) => (
                                                    <div
                                                        key={response.id}
                                                        className={`p-4 rounded-md ${
                                                            response.from === 'Support Team'
                                                                ? 'bg-blue-50 ml-4'
                                                                : 'bg-gray-50 mr-4'
                                                        }`}
                                                    >
                                                        <div className="flex justify-between items-start">
                              <span className="text-sm font-medium text-gray-900">
                                {response.from}
                              </span>
                                                            <span className="text-xs text-gray-500">
                                {response.createdAt}
                              </span>
                                                        </div>
                                                        <p className="mt-2 text-sm text-gray-700">{response.message}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {ticket.status !== 'Resolved' && ticket.status !== 'Closed' && (
                                            <div className="mt-4">
                                                <h4 className="text-sm font-medium text-gray-900">Reply:</h4>
                                                <div className="mt-2">
                          <textarea
                              rows={4}
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              placeholder="Type your response here..."
                              value={newResponse}
                              onChange={(e) => setNewResponse(e.target.value)}
                          />
                                                </div>
                                                <div className="mt-2 flex justify-end">
                                                    <Button
                                                        variant="primary"
                                                        onClick={() => handleSubmitResponse(ticket.id)}
                                                        disabled={!newResponse.trim()}
                                                    >
                                                        Send Response
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Card>
                    ))
                ) : (
                    <Card>
                        <div className="text-center py-6">
                            <p className="text-gray-500">No support tickets found.</p>
                        </div>
                    </Card>
                )}
            </div>

            {filteredTickets.length > itemsPerPage && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            )}
        </div>
    );
};

export default SupportTickets;